import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "../../hooks/useForm";
import { validateEmail } from "../../utils/validations";
import styles from "./CustomerLoginPage.module.scss";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCompany } from "../../context/CompanyContext";
import { useSubdomain } from "../../hooks/useSubdomain";
import { getCurrentSubdomain, isSubdomainEnvironment } from "../../utils/subdomainUtils";
import { API_BASE_URL } from "../../utils/constants";

interface LoginFormData {
  phone: string;
  password: string;
}

const validationRules = {
  phone: {
    required: true,
    custom: (value: string) => {
      if (!value.match(/^[0-9]{10}$/)) {
        return "Please enter a valid 10-digit phone number";
      }
      return null;
    },
  },
  password: {
    required: true,
    minLength: 6,
  },
};

const CustomerLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { name, companyId } = useCompany();
  const { company: subdomainCompany, loading: subdomainLoading } = useSubdomain();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyName, setCompanyName] = useState(name);
  const [subdomain, setSubdomain] = useState("");
  const [isSubdomainEnv, setIsSubdomainEnv] = useState(false);

  const { values, errors, touched, handleChange, handleBlur, validateForm } =
    useForm<LoginFormData>(
      {
        phone: "",
        password: "",
      },
      validationRules
    );

  // Initialize subdomain and company name
  useEffect(() => {
    const currentSubdomain = getCurrentSubdomain();
    const isSubdomain = isSubdomainEnvironment();

    setSubdomain(currentSubdomain);
    setIsSubdomainEnv(isSubdomain);

    if (subdomainCompany) {
      setCompanyName(subdomainCompany.name+ ' Customer Account');
    } else if (isSubdomain && currentSubdomain) {
      setCompanyName("Sentech Customer Account");
    } else {
      setCompanyName(name+ ' Customer Account');
    }
  }, [subdomainCompany, name]);

  // Login API
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_BASE_URL}/auth/customer-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: values.phone,
          pwd: values.password,
          company_id: companyId,
        }),
      });

      const data = await res.json();

      console.log("aafnlwfbabg::::::;", data)

      if (!data.status) {
        toast.error(data?.data?.error || "Invalid credentials");
        return;
      }

      toast.success("Login successful!");
      localStorage.setItem("customerToken", data.data.token);
      localStorage.setItem("customerId", data.data.user.id);
      localStorage.setItem("customerCompanyId", data.data.user.company_id);
      navigate("/customers/balance", { replace: true });
    } catch (error) {
      toast.error("Something went wrong!");
      console.error("Login error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.loginWrapper}>
      <div className={styles.loginLeft}>
        <div className={styles.loginCard}>
          <div className={styles.header}>
            {subdomainLoading ? <h1>Loading...</h1> : <h1>{companyName}</h1>}
            <p>Sign in to customer account</p>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="phone" className={styles.label}>
                Phone Number
              </label>
              <input
                id="phone"
                name="phone"
                value={values.phone}
                onChange={(e) => handleChange("phone")(e.target.value)}
                onBlur={() => handleBlur("phone")()}
                className={`${styles.input} ${errors.phone && touched.phone ? styles.error : ""}`}
                placeholder="Enter your phone number"
                disabled={isSubmitting}
              />
              {errors.phone && touched.phone && (
                <span className={styles.errorText}>{errors.phone}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password" className={styles.label}>
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={values.password}
                onChange={(e) => handleChange("password")(e.target.value)}
                onBlur={() => handleBlur("password")()}
                className={`${styles.input} ${errors.password && touched.password ? styles.error : ""}`}
                placeholder="Enter your password"
                disabled={isSubmitting}
              />
              {errors.password && touched.password && (
                <span className={styles.errorText}>{errors.password}</span>
              )}
            </div>

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className={styles.poweredBy}>
            <p>Powered by Sentech Technologies</p>
          </div>
          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </div>
    </div>
  );
};

export default CustomerLoginPage;
